import "./Modal.scss";
import { useEffect, useState } from "react";
import axios from "axios";
import { formatAmount } from "../Home/activityPrice";

const BACKEND = process.env.REACT_APP_BACKEND;
const EMPTY_FORM = { name: "", description: "", price: "", priceNote: "", prices: [], image: "" };
const EMPTY_ROW = { label: "", amount: "", currency: "EGP", unit: "" };
const CURRENCIES = ["EGP", "EUR", "USD"];
const UNIT_SUGGESTIONS = ["hour", "person", "session", "45 min", "90 min", "course"];

const priceSummary = (a) =>
  a.prices?.length
    ? a.prices.map((p) => `${p.label ? `${p.label}: ` : ""}${formatAmount(p)}${p.unit ? ` / ${p.unit}` : ""}`).join(" · ")
    : a.price;

function AddActivityModal({ open, onClose, onChanged }) {
  const [activities, setActivities] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      loadActivities();
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  const loadActivities = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/activities`);
      setActivities(await res.json());
    } catch {}
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImage(null);
    setEditingId(null);
  };

  const startEdit = (activity) => {
    setEditingId(activity._id);
    setForm({
      name: activity.name,
      description: activity.description,
      price: activity.price || "",
      priceNote: activity.priceNote || "",
      prices: (activity.prices || []).map((p) => ({ ...EMPTY_ROW, ...p, amount: String(p.amount) })),
      image: activity.image || "",
    });
    setImage(null);
  };

  const updateRow = (i, field, value) =>
    setForm((p) => ({ ...p, prices: p.prices.map((r, j) => (j === i ? { ...r, [field]: value } : r)) }));
  const addRow = () => setForm((p) => ({ ...p, prices: [...p.prices, { ...EMPTY_ROW }] }));
  const removeRow = (i) => setForm((p) => ({ ...p, prices: p.prices.filter((_, j) => j !== i) }));
  const moveRow = (i, dir) =>
    setForm((p) => {
      const rows = [...p.prices];
      const j = i + dir;
      if (j < 0 || j >= rows.length) return p;
      [rows[i], rows[j]] = [rows[j], rows[i]];
      return { ...p, prices: rows };
    });

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("price", form.price);
      fd.append("priceNote", form.priceNote);
      fd.append(
        "prices",
        JSON.stringify(
          form.prices
            .filter((r) => r.amount !== "" && !Number.isNaN(Number(r.amount)))
            .map((r) => ({ ...r, amount: Number(r.amount) }))
        )
      );
      if (image) fd.append("image", image);
      if (editingId) {
        await axios.put(`${BACKEND}/api/activities/${editingId}`, fd);
      } else {
        await axios.post(`${BACKEND}/api/activities`, fd);
      }
      await loadActivities();
      onChanged?.();
      resetForm();
    } catch {}
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await axios.delete(`${BACKEND}/api/activities/${id}`);
      await loadActivities();
      onChanged?.();
      if (editingId === id) resetForm();
    } catch {}
  };

  if (!open) return null;

  return (
    <div
      className="popup-overlay-admin"
      onClick={(e) => e.target.classList.contains("popup-overlay-admin") && onClose()}
    >
      <div className="popup-content">
        <button className="close-button" onClick={onClose}>&times;</button>
        <h1>Manage Activities</h1>

        {activities.length > 0 && (
          <div className="activity-manage-list">
            {activities.map((a) => (
              <div key={a._id} className={`activity-manage-row${editingId === a._id ? " editing" : ""}`}>
                {a.image && (
                  <img
                    src={`${BACKEND}/images/activities/${a.image}`}
                    alt={a.name}
                    className="activity-thumb"
                  />
                )}
                <div className="activity-manage-info">
                  <strong>{a.name}</strong>
                  <span>{priceSummary(a)}</span>
                </div>
                <div className="activity-manage-actions">
                  <button className="edit-btn" onClick={() => startEdit(a)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(a._id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activities.length === 0 && (
          <p style={{ color: "#474747", marginBottom: "12px" }}>No activities yet.</p>
        )}

        <hr style={{ margin: "16px 0", borderColor: "#333" }} />

        <h2 style={{ fontSize: "1rem", marginBottom: "12px" }}>
          {editingId ? `Edit: ${form.name}` : "Add Activity"}
        </h2>

        <div className="edit-info">
          <label>
            Name
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Kayaking"
            />
          </label>
          <label>
            Description
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Short description of the activity"
              rows={3}
            />
          </label>
          <div className="price-rows">
            <div className="price-rows-head">
              <span>Prices</span>
              <small>One row per option. Unit is optional, shown as "/ unit".</small>
            </div>
            {form.prices.map((row, i) => (
              <div key={i} className="price-row">
                <input
                  className="price-row-label"
                  value={row.label}
                  onChange={(e) => updateRow(i, "label", e.target.value)}
                  placeholder="Label, e.g. Single kayak"
                  aria-label="Price label"
                />
                <input
                  className="price-row-amount"
                  type="number"
                  min="0"
                  value={row.amount}
                  onChange={(e) => updateRow(i, "amount", e.target.value)}
                  placeholder="Amount"
                  aria-label="Amount"
                />
                <select
                  value={row.currency}
                  onChange={(e) => updateRow(i, "currency", e.target.value)}
                  aria-label="Currency"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  className="price-row-unit"
                  list="activity-unit-suggestions"
                  value={row.unit}
                  onChange={(e) => updateRow(i, "unit", e.target.value)}
                  placeholder="Unit, e.g. hour"
                  aria-label="Unit"
                />
                <div className="price-row-actions">
                  <button type="button" onClick={() => moveRow(i, -1)} disabled={i === 0} aria-label="Move up">
                    <i className="fa-solid fa-arrow-up" aria-hidden="true"></i>
                  </button>
                  <button type="button" onClick={() => moveRow(i, 1)} disabled={i === form.prices.length - 1} aria-label="Move down">
                    <i className="fa-solid fa-arrow-down" aria-hidden="true"></i>
                  </button>
                  <button type="button" className="remove" onClick={() => removeRow(i)} aria-label="Remove price">
                    <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            ))}
            <datalist id="activity-unit-suggestions">
              {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
            </datalist>
            <button type="button" className="add-price-row" onClick={addRow}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i> Add price
            </button>
          </div>
          <label>
            Price note
            <textarea
              value={form.priceNote}
              onChange={(e) => setForm((p) => ({ ...p, priceNote: e.target.value }))}
              placeholder="e.g. Prices are per person. Minimum 2 participants."
              rows={2}
              maxLength={300}
            />
          </label>
          {form.prices.length === 0 && (
            <label>
              Price (free text, used only when no price rows are added)
              <input
                value={form.price}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                placeholder="e.g. 500 EGP/hour"
              />
            </label>
          )}
          <label>
            Image
            {editingId && form.image && !image && (
              <img
                src={`${BACKEND}/images/activities/${form.image}`}
                alt={form.name}
                style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 4, display: "block", marginBottom: 8 }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImage(e.target.files[0] || null)}
            />
            {image && <span className="file-count">{image.name}</span>}
          </label>
        </div>

        <div className="modal-footer">
          {editingId && (
            <button className="cancel-btn" onClick={resetForm}>Cancel Edit</button>
          )}
          <button className="cancel-btn" onClick={onClose}>Close</button>
          <button className="save-btn" onClick={handleSave} disabled={loading}>
            {editingId ? "Save Changes" : "Add Activity"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddActivityModal;
